/**
 * useAuth Hook
 * 
 * Provides authentication state and user info.
 * NO tokens are stored - only session_id in HttpOnly cookie.
 */

import { useState, useEffect } from "react";
import { getCurrentUser, getCurrentUserOptional, type User } from "../services/authApi";
import { isPublicRoute } from "../constants/publicRoutes";

interface UseAuthReturn {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchUser = async (retryCount = 0) => {
    // Check if we're on a public route - use optional endpoint for public routes
    const currentPath = window.location.pathname;
    const isPublic = isPublicRoute(currentPath);
    
    // Check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const logoutParam = urlParams.get("logout");
    const isLogout = logoutParam === "true";  // Strict equality check for security
    const loginSuccessParam = urlParams.get("login_success");
    const isLoginSuccess = loginSuccessParam === "true";  // Strict equality check for security
    
    // CRITICAL FIX: If login_success parameter is present, clear logout_flag immediately
    // This ensures that logout_flag doesn't prevent authentication after successful login
    if (isLoginSuccess) {
      localStorage.removeItem("logout_flag");
      // Clean up login_success parameter from URL immediately
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
    }
    
    // Check if this is a logout redirect - if so, skip authentication check
    // Security: Only accept exact "true" value, ignore any other values
    
    // If logout parameter is present, force unauthenticated state
    // This is safe because session is already deleted from Redis by backend
    if (isLogout) {
      // Clean up logout parameter from URL immediately
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
      
      // CRITICAL FIX: Set logout flag in localStorage to prevent auto-redirect
      // Use localStorage instead of sessionStorage so it persists across page refreshes
      // This ensures that even if cookie is still present (due to browser delay),
      // we won't treat user as authenticated until they log in again
      localStorage.setItem("logout_flag", "true");
      
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
      return;
    }
    
    // CRITICAL FIX: Check logout flag before fetching user
    // If logout flag is set, don't fetch user even if cookie is present
    // Clear logout flag only when user successfully logs in (handled in callback)
    const logoutFlag = localStorage.getItem("logout_flag");
    if (logoutFlag === "true") {
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Small delay on first attempt to allow cookie to be set after redirect
      if (retryCount === 0) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Use optional endpoint for public routes (doesn't throw 401)
      // Use required endpoint for protected routes (throws 401 if not authenticated)
      const currentUser = isPublic 
        ? await getCurrentUserOptional()
        : await getCurrentUser();
      
      if (currentUser) {
        setUser(currentUser);
        setIsAuthenticated(true);
        // CRITICAL FIX: Clear logout_flag on successful authentication
        // This ensures that logout_flag doesn't persist after login
        localStorage.removeItem("logout_flag");
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (err) {
      // Retry once if first attempt fails (cookie might not be ready yet)
      // Only retry for protected routes, not public routes
      if (!isPublic && retryCount === 0 && err instanceof Error) {
        const axiosError = err as any;
        // Only retry on 401 (not authenticated) - not on network errors
        if (axiosError.response?.status === 401) {
          await new Promise(resolve => setTimeout(resolve, 500));
          return fetchUser(1);
        }
      }
      
      // Suppress errors for public routes (401 is expected)
      // Don't set error for public routes (401 is expected)
      if (!isPublic || (err instanceof Error && err.name !== "SilentAuthError")) {
        const authError = err instanceof Error ? err : new Error("Authentication failed");
        setError(authError);
      }
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    refetch: fetchUser,
  };
}

